import api from '../../api/client';
// components/AddCustomerCreditModal.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Form,
  InputNumber,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Alert,
  message,
} from "antd";
import { DollarOutlined, CreditCardOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import config from "../../config";
import { groupAccountOptions } from "../../utils/account";

const { TextArea } = Input;
const { Option } = Select;

const AddCustomerCreditModal = ({
  visible,
  customerId,
  customerName,
  onClose,
  onSuccess,
  currentUser,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [paidAccounts, setPaidAccounts] = useState([]);

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Fetch paid-through accounts for the form (used in select options)
  const fetchPaidAccounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/paid-through`);
      setPaidAccounts(data);
    } catch (error) {
      console.error("Failed to fetch paid-through accounts", error);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchPaidAccounts();
  }, [fetchPaidAccounts]);

  const paidOptions = groupAccountOptions(paidAccounts); // from your utils
const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const response = await api.post(`/customer-credits`, {
        customer_id: customerId,
        amount: values.amount,
        payment_method: values.payment_method,
        payment_date: values.payment_date.format("YYYY-MM-DD"),
        notes: values.notes,
        expiry_days: values.expiry_days,
        transaction_type: values.transaction_type || "prepayment",
        user_id: currentUser?.id,
        user_name: currentUser?.firstname,
        debitAccount: values.debitAccount,
      });

      if (response.data.success) {
        message.success(response.data.message);
        form.resetFields();
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error("Error adding credit:", error);
      message.error(error.response?.data?.error || "Failed to add credit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <CreditCardOutlined />
          <span>Add Customer Credit - {customerName}</span>
        </Space>
      }
      open={visible}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      footer={null}
      width={500}
    >
      <Alert
        message="Customer Credit"
        description="Adding credit will allow the customer to use this amount for future purchases."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          transaction_type: "prepayment",
          payment_date: dayjs(),
          expiry_days: null,
        }}
      >
        <Form.Item name="transaction_type" label="Credit Type">
          <Select>
            <Option value="prepayment">
              Prepayment (Customer paid in advance)
            </Option>
            <Option value="overpayment">
              Overpayment (Excess from previous sale)
            </Option>
            <Option value="credit_note">Credit Note (Refund/Adjustment)</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="amount"
          label="Amount"
          rules={[{ required: true, message: "Please enter amount" }]}
        >
          <InputNumber
            min={0}
            step={100}
            style={{ width: "100%" }}
            prefix="KES"
            placeholder="Enter amount"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="payment_method"
          label="Payment Method"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select payment method">
            <Option value="cash">Cash</Option>
            <Option value="mpesa">M-Pesa</Option>
            <Option value="bank_transfer">Bank Transfer</Option>
            <Option value="cheque">Cheque</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="payment_date"
          label="Payment Date"
          rules={[{ required: true }]}
        >
          <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
        </Form.Item>

        <Form.Item
          name="debitAccount"
          label="Debit Account"
          rules={[
            {
              required: true,
              message: "Please select debit account",
            },
          ]}
        >
          <Select
            options={paidOptions}
            placeholder="Select debit account"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          name="expiry_days"
          label="Credit Expiry (Days)"
          tooltip="Leave empty for no expiry"
        >
          <InputNumber
            min={1}
            style={{ width: "100%" }}
            placeholder="e.g., 365 for one year"
          />
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <TextArea
            rows={3}
            placeholder="Reason for credit, reference number, etc."
          />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Add Credit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddCustomerCreditModal;
